import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import { ReactNode } from "react";
import aboutus from "@/public/images/aboutus.jpg";
import logo from "@/public/images/smallLogo.png";
import star from "@/public/images/starBlack.svg";
import time from "@/public/images/time.svg";
import user from "@/public/images/user.svg";
import trueCircle from "@/public/images/trueCircle.svg";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@radix-ui/react-dialog";

import { useTranslate } from "@/components/base/providers/TranslateProv";
import { getsingleCertificate } from "@/public/Data/CertificatesLable";
import Link from "next/link";

interface Props {
  children: any;
  university: any;
}

const ApplicationInformation = ({ children, university }: Props) => {
  const { locale }: { locale: "ar" | "en" } = useTranslate(); // Get the current locale
  const [applicationType, setApplicationType] = useState<
    "egyptian" | "international"
  >("egyptian");

  const isArabic = locale === "ar";

  const currentInfo =
    applicationType === "egyptian"
      ? university?.application_information
      : university?.application_information_outsite;

  return (
    <>
      <Dialog>
        <DialogTrigger className="w-full">{children}</DialogTrigger>
        <DialogContent className="overflow-y-auto w-full md:w-[800px] overflow-x-hidden max-h-screen p-5">
          <DialogHeader>
            <div className="relative mx-auto">
              <Image
                src={university?.image}
                width={130}
                height={130}
                style={{
                  border: "8px solid #F8F9FA",
                }}
                alt={isArabic ? "عننا" : "About Us"}
                className="rounded-full w-28 h-28 object-cover "
              />
              <Badge className="absolute -top-4 -right-[8px] bg-transparent">
                <Image
                  src={logo}
                  alt={isArabic ? "شعار" : "Logo"}
                  className="w-9"
                />
              </Badge>
            </div>
          </DialogHeader>
          <DialogDescription>
            <div className=" w-full">
              <div className="border border-gray-200 rounded-[15px] bg-[#FFF] p-2 ">
                <h3 className="text-[#141522] text-center font-medium text-[20px] leading-normal mb-[15px] mx-2 border-b p-1 pb-2">
                  {university?.name[locale]}
                </h3>

                <div className="bg-[#F8F9FA] w-full items-center justify-center p-2.5 flex gap-2.5 rounded-[10px] mb-4">
                  <button
                    onClick={() => setApplicationType("egyptian")}
                    className={`p-[14px] text-[18px] text-center w-full rounded-[10px] transition-colors duration-300 ${
                      applicationType === "egyptian"
                        ? "bg-[#272A38] text-white"
                        : "text-black hover:bg-gray-100"
                    }`}
                  >
                    {isArabic ? "التقديم للمصريين" : "Apply for Egyptians"}
                  </button>
                  <button
                    onClick={() => setApplicationType("international")}
                    className={`p-[14px] text-[18px] text-center w-full rounded-[10px] transition-colors duration-300 ${
                      applicationType === "international"
                        ? "bg-[#272A38] text-white"
                        : "text-black hover:bg-gray-100"
                    }`}
                  >
                    {isArabic ? "التقديم للوافدين" : "Apply for International"}
                  </button>
                </div>

                <div
                  style={{
                    boxShadow: " 0px 4px 54px 0px rgba(0, 0, 0, 0.05)",
                  }}
                  className="m-2 my-[15px] border text-darkText text-[14px] border-[#EBEBEB] bg-white rounded-[15px] text-center p-[20px]"
                >
                  {currentInfo?.description?.[locale] || (isArabic ? "لا توجد معلومات حالياً" : "No information available currently")}
                </div>

                <ul className=" m-2 border border-[#EBEBEB] bg-white rounded-[15px] flex flex-col gap-3  p-5">
                  {currentInfo?.advantages?.length > 0 ? (
                    currentInfo.advantages.map((item: any, index: number) => (
                      <li key={index} className="flex items-center ">
                        <Image
                          src={trueCircle}
                          alt={isArabic ? "true circle" : "check"}
                          className="ml-2"
                        />
                        <p className="text-darkText text-[13px]">
                          {item?.title?.[locale] || item?.[locale]}
                        </p>
                      </li>
                    ))
                  ) : (
                    <p className="text-center text-descriptionText text-14">
                      {isArabic ? "لا توجد مميزات مسجلة" : "No advantages recorded"}
                    </p>
                  )}
                </ul>
              </div>
              <div className="grid grid-cols-3  text-center gap-3 mt-3">
                <div className=" bg-[#F8F9FA] text-center  rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                  <Image
                    src={star}
                    alt={isArabic ? "نوع الشهاده" : "certificate type"}
                  />
                  <p className="text-13 text-darkText">
                    {isArabic ? "نوع الشهاده" : "certificate type"}
                  </p>
                  <p className="text-[12px] text-center text-[#ACB5BB] font-[600]">
                    {currentInfo?.certificate_type?.[locale] || "---"}
                  </p>
                </div>
                <div className=" bg-[#F8F9FA] text-center  rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                  <Image src={time} alt={isArabic ? "الوقت" : "Time"} />
                  <p className="text-13 text-darkText">
                    {isArabic
                      ? " الموعد النهائي للتقديم"
                      : "Final submission time"}
                  </p>
                  <p className="text-13 text-[#FF4C51] font-[600]">
                    {currentInfo?.application_deadline || "---"}
                  </p>
                </div>
                <div className=" bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                  <Image
                    src={user}
                    alt={isArabic ? "للعام الدراسي" : "Academic Year"}
                  />
                  <p className="text-13 text-darkText">
                    {isArabic ? "للعام الدراسي" : "Academic Year"}
                  </p>
                  <p className="text-13 text-[#ACB5BB] font-[600]">
                    {currentInfo?.academic_year || "---"}
                  </p>
                </div>
              </div>

              {currentInfo?.application_link && (
                <Link
                  href={currentInfo.application_link}
                  target="_blank"
                  className=" mt-[15px]  bg-primaryColor text-16 text-white font-[500] flex items-center justify-center rounded-[10px] h-[58px] w-full"
                >
                  {locale === "ar"
                    ? "رابط التقديم بالجامعه"
                    : "University application link"}
                </Link>
              )}
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApplicationInformation;
